import React from "react";
import { Link } from "react-router-dom";
import styles from "./AiFeaturesPage.module.css";

// ─── Example messages for Before / After comparison ───────────────────────────
const TEMPLATE_EXAMPLE = `Hi John,

I hope you're doing well! I recently spotted the
Software Engineer opening at Google and it
immediately caught my attention.

I have 3 years of experience, with strong
proficiency in React, Node.js. I completed my
education at IIT Delhi. I believe my background
aligns well with what Google needs for this role.

Would you be comfortable referring me for this
position? It would mean a great deal to me.

Thank you so much for your time!`;

const AI_EXAMPLE = `Hey John,

Hope things are great at Google! The SWE role on
the Search Infrastructure team caught my eye —
honestly, distributed systems at Google's scale is
exactly the problem space I want to be in.

Quick snapshot: 3 yrs full-stack, shipped
React micro-frontends at scale, led a Node.js
migration that cut API latency by 40%. IIT Delhi
grad, obsessive about performance.

Would you be open to a referral? Zero pressure —
even a nudge to the right recruiter would be huge.

Cheers!`;

// ─── Feature cards ─────────────────────────────────────────────────────────────
const FEATURES = [
  {
    icon: "⚡",
    title: "Hyper-Personalized",
    description:
      "Reads every detail — company, role, your background — and crafts language that feels written just for that one person.",
    color: "#f59e0b",
    bg: "#fef3c7",
  },
  {
    icon: "🎯",
    title: "Response-Optimized",
    description:
      "Trained on what actually works. The right tone, the right length, the right hooks that make referrers say yes.",
    color: "#2563eb",
    bg: "#eff6ff",
  },
  {
    icon: "✏️",
    title: "Fully Editable",
    description:
      "Get a crisp draft in seconds, then fine-tune anything. AI does the hard work — you add the final human touch.",
    color: "#10b981",
    bg: "#f0fdf4",
  },
  {
    icon: "🔄",
    title: "Unlimited Rewrites",
    description:
      "Not feeling the first result? Hit generate again. Every output is freshly crafted — never a copy-paste.",
    color: "#8b5cf6",
    bg: "#f5f3ff",
  },
];

// ─── Stats ─────────────────────────────────────────────────────────────────────
const STATS = [
  { value: "10,000+", label: "Referrals Generated" },
  { value: "3×", label: "More Replies vs Template" },
  { value: "< 2 min", label: "Average Gen Time" },
  { value: "Free", label: "To Get Started" },
];

// ─── How it works steps ────────────────────────────────────────────────────────
const STEPS = [
  { num: "01", title: "Fill your details", desc: "Company, role, your experience, skills — takes 60 seconds." },
  { num: "02", title: "Hit Generate", desc: "Gemini AI crafts a personalized, response-optimized message instantly." },
  { num: "03", title: "Send & land the referral", desc: "Copy, tweak if you like, and send. Watch the replies come in." },
];

export default function AiFeaturesPage() {
  return (
    <div className={styles.page}>

      {/* ── Top bar ── */}
      <div className={styles.topBar}>
        <Link to="/" className={styles.backLink}>
          ← Back to Generator
        </Link>
      </div>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.aiPill}>
            <span className={styles.aiPillDot} />
            Powered by Gemini AI
          </div>

          <h1 className={styles.heroTitle}>
            Stop Sending<br />
            <span className={styles.heroGradient}>Generic Messages.</span>
          </h1>

          <p className={styles.heroSubtitle}>
            AI-crafted referral requests that are personalized, compelling, and
            actually get responses — in under 2 minutes.
          </p>

          <div className={styles.heroCtas}>
            <Link to="/signup" className={styles.ctaPrimary}>
              ✨ Sign Up Free — It's Instant
            </Link>
            <Link to="/login" className={styles.ctaSecondary}>
              Already have an account? Log In →
            </Link>
          </div>

          {/* floating decoration dots */}
          <span className={`${styles.heroDot} ${styles.heroDot1}`} />
          <span className={`${styles.heroDot} ${styles.heroDot2}`} />
          <span className={`${styles.heroDot} ${styles.heroDot3}`} />
        </div>
      </section>

      {/* ── BEFORE / AFTER ── */}
      <section className={styles.section}>
        <p className={styles.sectionLabel}>THE DIFFERENCE IS REAL</p>
        <h2 className={styles.sectionTitle}>Template vs. AI Generation</h2>
        <p className={styles.sectionSubtitle}>
          Same inputs. Completely different impact.
        </p>

        <div className={styles.compareGrid}>
          {/* Before */}
          <div className={`${styles.compareCard} ${styles.compareBefore}`}>
            <div className={styles.compareCardHeader}>
              <span className={styles.compareBadgeBefore}>📄 Template Preview</span>
              <span className={styles.compareCardNote}>What you get without signing in</span>
            </div>
            <pre className={styles.compareText}>{TEMPLATE_EXAMPLE}</pre>
            <div className={styles.compareFlaws}>
              <span className={styles.flaw}>✗ Generic phrasing</span>
              <span className={styles.flaw}>✗ Robotic structure</span>
              <span className={styles.flaw}>✗ Easy to ignore</span>
            </div>
          </div>

          {/* VS divider */}
          <div className={styles.vsDivider} aria-hidden="true">VS</div>

          {/* After */}
          <div className={`${styles.compareCard} ${styles.compareAfter}`}>
            <div className={styles.compareCardHeader}>
              <span className={styles.compareBadgeAfter}>✨ AI Generated</span>
              <span className={styles.compareCardNote}>What you unlock by signing up</span>
            </div>
            <pre className={styles.compareText}>{AI_EXAMPLE}</pre>
            <div className={styles.compareWins}>
              <span className={styles.win}>✓ Natural, human tone</span>
              <span className={styles.win}>✓ Specific to role & person</span>
              <span className={styles.win}>✓ Hard to say no to</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div className={styles.statsBar}>
        {STATS.map((s) => (
          <div className={styles.stat} key={s.label}>
            <span className={styles.statValue}>{s.value}</span>
            <span className={styles.statLabel}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── FEATURES ── */}
      <section className={styles.section}>
        <p className={styles.sectionLabel}>WHY AI WINS</p>
        <h2 className={styles.sectionTitle}>Everything a template can't do</h2>

        <div className={styles.featuresGrid}>
          {FEATURES.map((f) => (
            <div className={styles.featureCard} key={f.title}>
              <span
                className={styles.featureIcon}
                style={{ background: f.bg, color: f.color }}
              >
                {f.icon}
              </span>
              <h3 className={styles.featureTitle}>{f.title}</h3>
              <p className={styles.featureDesc}>{f.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className={`${styles.section} ${styles.sectionAlt}`}>
        <p className={styles.sectionLabel}>DEAD SIMPLE</p>
        <h2 className={styles.sectionTitle}>Three steps to your referral</h2>

        <div className={styles.stepsRow}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.num}>
              <div className={styles.step}>
                <span className={styles.stepNum}>{s.num}</span>
                <h3 className={styles.stepTitle}>{s.title}</h3>
                <p className={styles.stepDesc}>{s.desc}</p>
              </div>
              {i < STEPS.length - 1 && (
                <span className={styles.stepArrow} aria-hidden="true">→</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className={styles.ctaSection}>
        <div className={styles.ctaInner}>
          <h2 className={styles.ctaTitle}>
            Your dream job deserves a<br />
            <span className={styles.ctaTitleAccent}>great first impression.</span>
          </h2>
          <p className={styles.ctaSubtext}>
            Free to sign up. No credit card. No catch. Just AI-crafted
            messages that land referrals.
          </p>
          <Link to="/signup" className={styles.ctaPrimaryLarge}>
            ✨ Get AI Access — It's Free
          </Link>
          <p className={styles.ctaLoginText}>
            Have an account?{" "}
            <Link to="/login" className={styles.ctaLoginLink}>Sign in →</Link>
          </p>
        </div>
      </section>

    </div>
  );
}
